import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Loading from '../../../components/loading';
import { useAestheticProcedures } from './hooks/useAestheticProcedures';
import { useAestheticProcedureActions } from './hooks/useAestheticProcedureActions';
import ErrorMessage from '../../../components/errorMessage';
import SuccessMessage from '../../../components/successMessage';
import AestheticalProcedureModal from './aestheticalProcedureModal';
import ProcedureApplicationMapWeb from './procedureApplicationMapWeb';
import ProcedureApplicationMapMobile from './procedureApplicationMapMobile';

const TYPE_LABELS = {
  INJECTABLE_APPLICATION: 'Injetável (Mapa)',
  PARAMETER: 'Parâmetro',
};

export default function AestheticalProtocolProcedures({
  baseUrl,
  applicationsResource,
  isMobile,
  title,
  subtitle,
  showCount,
}) {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [procedures, setProcedures] = useState([]);
  const [protocolTotalAmount, setProtocolTotalAmount] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [procedureToEdit, setProcedureToEdit] = useState(null);

  const { data: proceduresData, refetch: fetchProcedures } = useAestheticProcedures(baseUrl);
  const { finish: finishRequest, remove: removeRequest } = useAestheticProcedureActions(baseUrl);

  useEffect(() => {
    setProcedures(proceduresData?.procedures ?? []);
    setProtocolTotalAmount(proceduresData?.totalAmountFromAllProceduresFormatted ?? '');
  }, [proceduresData]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [procedureForActions, setProcedureForActions] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [finishModal, setFinishModal] = useState(false);

  const [applicationMapModal, setApplicationMapModal] = useState(false);
  const [mapProcedure, setMapProcedure] = useState(null);

  const handleSaveSuccess = (message) => {
    setIsModalOpen(false);
    setProcedureToEdit(null);
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    fetchProcedures();
  };

  const handleAddClick = () => {
    setProcedureToEdit(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setProcedureToEdit(null);
  };

  const handleViewApplicationMap = (procedure) => {
    setMapProcedure(procedure);
    setApplicationMapModal(true);
  };

  const handleMenuOpen = (event, procedure) => {
    setAnchorEl(event.currentTarget);
    setProcedureForActions(procedure);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setProcedureForActions(null);
  };

  const handleEditClick = () => {
    setProcedureToEdit(procedureForActions);
    setIsModalOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setDeleteModal(true);
    setAnchorEl(null);
  };

  const handleFinishClick = () => {
    setFinishModal(true);
    setAnchorEl(null);
  };

  const handleConfirmFinish = () => {
    setLoading(true);
    finishRequest(procedureForActions.id)
      .then((returnFromApi) => {
        setSuccessMessage(returnFromApi.data);
        setShowSuccessMessage(true);
        setFinishModal(false);
        setProcedureForActions(null);
        fetchProcedures();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setLoading(false));
  };

  const handleConfirmDelete = () => {
    setLoading(true);
    removeRequest(procedureForActions.id)
      .then((returnFromApi) => {
        setSuccessMessage(returnFromApi.data);
        setShowSuccessMessage(true);
        setDeleteModal(false);
        setProcedureForActions(null);
        fetchProcedures();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
        <CardHeader
          title={
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {title}
              {showCount ? ` (${procedures.length})` : ''}
            </Typography>
          }
          subheader={
            subtitle ? (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null
          }
          action={
            <Button variant="outlined" size="small" onClick={handleAddClick} disabled={!baseUrl}>
              Adicionar Procedimento
            </Button>
          }
          sx={{ py: 1, px: 2 }}
        />
        <CardContent sx={{ pt: 0 }}>
          {procedures.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
              Nenhum procedimento cadastrado ainda.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Procedimento</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data de realização</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {procedures.map((procedure) => (
                    <TableRow key={procedure.id} hover>
                      <TableCell>
                        <Typography variant="body2">{procedure.procedureDescription}</Typography>
                        {procedure.observation && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {procedure.observation}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{TYPE_LABELS[procedure.type] ?? procedure.type}</TableCell>
                      <TableCell>{procedure.amountFormatted}</TableCell>
                      <TableCell>{procedure.executionDate}</TableCell>
                      <TableCell>
                        {procedure.statusDescription && (
                          <Chip label={procedure.statusDescription} color="info" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 8,
                          }}
                        >
                          {procedure.type === 'INJECTABLE_APPLICATION' && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewApplicationMap(procedure)}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Ver Mapa de Aplicação
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            onClick={(event) => handleMenuOpen(event, procedure)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      align="right"
                      sx={{ fontWeight: 'bold', color: 'text.primary', borderBottom: 'none' }}
                    >
                      Total:
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 'bold', color: 'text.primary', borderBottom: 'none' }}
                    >
                      {protocolTotalAmount}
                    </TableCell>
                    <TableCell colSpan={3} sx={{ borderBottom: 'none' }} />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>Editar Procedimento</MenuItem>
        <MenuItem onClick={handleFinishClick}>Finalizar Procedimento</MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          Excluir Procedimento
        </MenuItem>
      </Menu>

      <Dialog open={finishModal} onClose={() => setFinishModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Finalizar Procedimento</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Após finalizado, o procedimento não poderá mais ser alterado. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinishModal(false)}>Cancelar</Button>
          <Button variant="contained" disableElevation onClick={handleConfirmFinish}>
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteModal} onClose={() => setDeleteModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir Procedimento</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Ao excluir o procedimento, todas as aplicações registradas nele serão removidas
            permanentemente. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModal(false)}>Cancelar</Button>
          <Button variant="contained" disableElevation color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={applicationMapModal}
        onClose={() => setApplicationMapModal(false)}
        maxWidth={isMobile ? false : 'lg'}
        fullWidth={!isMobile}
        fullScreen={isMobile}
      >
        <DialogTitle>
          Mapa de Aplicação
          {mapProcedure?.procedureDescription ? ` — ${mapProcedure.procedureDescription}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {applicationMapModal &&
            mapProcedure &&
            (isMobile ? (
              <ProcedureApplicationMapMobile
                applicationsBaseUrl={`${applicationsResource}/${mapProcedure.id}/applications`}
                initialMapType={mapProcedure.mapType}
              />
            ) : (
              <ProcedureApplicationMapWeb
                applicationsBaseUrl={`${applicationsResource}/${mapProcedure.id}/applications`}
                initialMapType={mapProcedure.mapType}
              />
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationMapModal(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <AestheticalProcedureModal
        open={isModalOpen}
        onClose={handleCloseModal}
        baseUrl={baseUrl}
        procedure={procedureToEdit}
        onSave={handleSaveSuccess}
      />
    </>
  );
}

AestheticalProtocolProcedures.propTypes = {
  baseUrl: PropTypes.string,
  applicationsResource: PropTypes.string,
  isMobile: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  showCount: PropTypes.bool,
};

AestheticalProtocolProcedures.defaultProps = {
  baseUrl: null,
  applicationsResource: '/aesthetic-protocol-procedures',
  isMobile: false,
  title: 'Procedimentos',
  subtitle: null,
  showCount: true,
};
